import { useEffect, useRef, useState } from 'react';
import { monogram } from '../lib/format';

/**
 * The signed-in name in the top bar, as a menu.
 *
 * Signing out used to live only at the bottom of the profile tab, which is a long way to go for the
 * thing people look for first when they want to leave. The name is where they look, so the name is
 * where it is.
 *
 * `defaultOpen` exists so the menu can be rendered open in a test; nothing in the app passes it.
 */
export function UserMenu({ name, label, onSignOut, defaultOpen = false }: { name: string; label: string; onSignOut: () => void; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    addEventListener('mousedown', onDown);
    addEventListener('keydown', onKey);
    return () => {
      removeEventListener('mousedown', onDown);
      removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="who-wrap" ref={wrap}>
      <button type="button" className="who" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="avatar avatar-sm" aria-hidden="true">
          {monogram(name)}
        </span>
        <span className="who-name">{label}</span>
        <span className="who-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <div className="who-menu" role="menu">
          <a role="menuitem" href="#/profile" onClick={() => setOpen(false)}>
            Profile and care plan
          </a>
          <a role="menuitem" href="#/inbox" onClick={() => setOpen(false)}>
            Messages
          </a>
          <a role="menuitem" href="#/devices" onClick={() => setOpen(false)}>
            Connected devices
          </a>
          <button
            type="button"
            role="menuitem"
            className="danger"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
